import { Keypair } from '@solana/web3.js'
import bs58 from 'bs58'

/**
 * Reading a pasted private key well enough to say whose wallet it is.
 *
 * Importing a key blind is the part of this that goes wrong: somebody pastes
 * the wrong line out of a password manager, presses import, and finds out
 * which wallet it was by the balance that does not appear. So the key is
 * parsed here, in the browser, only to derive its public address — which is
 * shown before anything is sent. Axiom's import does not do this; it takes the
 * key and tells you afterwards.
 *
 * Three shapes reach us, because three tools export three ways: base58 (what
 * Phantom and most wallets give), a JSON array of 64 bytes (solana-keygen),
 * and hex (occasionally, from scripts). All three are the same 64 bytes.
 *
 * Nothing here logs, stores or returns the secret. The only thing that leaves
 * is the address, which is public by definition.
 */

export type KeyReading =
  | { ok: true; address: string }
  | { ok: false; reason: KeyProblem }

export type KeyProblem =
  | 'empty'
  | 'unreadable'
  /** Read, but not 64 bytes — a public key or a truncated paste. */
  | 'wrong-length'
  /** Sixty-four bytes that are not a valid keypair. */
  | 'not-a-key'
  /** Read fine, but the same wallet is already on a line above. */
  | 'duplicate'

const SECRET_BYTES = 64

/**
 * The bytes, whichever of the three ways they were written.
 *
 * Every reading is tried and the one that yields sixty-four bytes wins, rather
 * than choosing by the look of the string. The alphabets overlap: a base58 key
 * made only of the characters hex also uses would be read as hex and come out
 * the wrong length, which is a rare paste that would have been impossible to
 * explain.
 */
const bytesOf = (raw: string): Uint8Array | undefined => {
  const text = raw.trim()
  const tried = [asJson(text), asHex(text), asBase58(text)].filter(
    (bytes): bytes is Uint8Array => bytes !== undefined,
  )

  return tried.find((bytes) => bytes.length === SECRET_BYTES) ?? tried[0]
}

const asJson = (text: string): Uint8Array | undefined => {
  if (!text.startsWith('[')) {
    return undefined
  }
  try {
    const parsed: unknown = JSON.parse(text)
    if (
      !Array.isArray(parsed) ||
      parsed.some(
        (byte) =>
          typeof byte !== 'number' ||
          !Number.isInteger(byte) ||
          byte < 0 ||
          byte > 255,
      )
    ) {
      return undefined
    }
    return Uint8Array.from(parsed as Array<number>)
  } catch {
    return undefined
  }
}

const asHex = (text: string): Uint8Array | undefined => {
  const hex = text.replace(/^0x/i, '')
  if (!/^[\da-f]+$/i.test(hex) || hex.length % 2 !== 0) {
    return undefined
  }
  const out = new Uint8Array(hex.length / 2)
  for (let index = 0; index < out.length; index += 1) {
    out[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16)
  }
  return out
}

const asBase58 = (text: string): Uint8Array | undefined => {
  try {
    return bs58.decode(text)
  } catch {
    return undefined
  }
}

/**
 * What address this key controls, or why it cannot be read.
 *
 * The reasons are kept apart because they call for different words: a paste
 * that is not a key at all is a different mistake from a public address pasted
 * where the private key goes, and the second one is the common one.
 */
export const readKey = (raw: string): KeyReading => {
  if (raw.trim() === '') {
    return { ok: false, reason: 'empty' }
  }

  const bytes = bytesOf(raw)
  if (bytes === undefined) {
    return { ok: false, reason: 'unreadable' }
  }
  if (bytes.length !== SECRET_BYTES) {
    return { ok: false, reason: 'wrong-length' }
  }

  try {
    return {
      ok: true,
      address: Keypair.fromSecretKey(bytes).publicKey.toBase58(),
    }
  } catch {
    return { ok: false, reason: 'not-a-key' }
  }
}

export const keyProblemText: Record<KeyProblem, string> = {
  empty: 'Nothing pasted yet.',
  unreadable:
    'Not a key we can read. Base58, a JSON array of 64 numbers, or hex.',
  'wrong-length':
    'That is the right alphabet but the wrong length — a wallet address is not a private key.',
  'not-a-key': 'Sixty-four bytes, but not a valid key.',
  duplicate: 'The same wallet is on an earlier line.',
}

export type ImportLine = {
  /** Where it sat in the box, so a message can point at it. */
  index: number
  raw: string
  reading: KeyReading
}

/**
 * One key per line, read all at once.
 *
 * Somebody moving to us has six wallets somewhere else, and importing them one
 * modal at a time is the reason they stop after two. Blank lines are ignored
 * rather than counted as mistakes, and a repeated key is marked so the same
 * wallet does not arrive twice.
 */
export const readKeys = (text: string): ReadonlyArray<ImportLine> => {
  const seen = new Set<string>()

  return text
    .split('\n')
    .map((raw, index) => ({ index, raw }))
    .filter((line) => line.raw.trim() !== '')
    .map((line) => {
      const reading = readKey(line.raw)

      if (reading.ok && seen.has(reading.address)) {
        return { ...line, reading: { ok: false, reason: 'duplicate' } }
      }
      if (reading.ok) {
        seen.add(reading.address)
      }

      return { ...line, reading }
    })
}

/** Only the lines worth sending, in the order they were written. */
export const readableKeys = (
  lines: ReadonlyArray<ImportLine>,
): ReadonlyArray<{ raw: string; address: string }> =>
  lines.flatMap((line) =>
    line.reading.ok
      ? [{ raw: line.raw.trim(), address: line.reading.address }]
      : [],
  )
